import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { createAppTheme } from "../theme";
import { formatNumber, setFormatLocale } from "../lib/format";
import { fa, type StringKey } from "./fa";
import { en } from "./en";
import { ps } from "./ps";

export type Locale = "fa" | "ps" | "en";

/** Picker order; `label` is each language's own endonym. */
export const LOCALES: { code: Locale; label: string }[] = [
  { code: "fa", label: "دری" },
  { code: "ps", label: "پښتو" },
  { code: "en", label: "English" },
];

const RTL_LOCALES: readonly Locale[] = ["fa", "ps"];
const DICTS: Record<Locale, Record<StringKey, string>> = { fa, en, ps };
const STORAGE_KEY = "talar.locale";

/**
 * MUI positions many things with physical left/right (input labels and their
 * outline notch, icon spacing, drawers). Its supported RTL setup runs every
 * style through stylis-plugin-rtl, which mirrors left↔right. Without it, form
 * labels sat on the wrong side and icons stuck to their text. One emotion
 * cache per direction, created once; styles are authored LTR and mirrored for
 * Dari/Pashto, so English gets a true left-to-right layout from the same code.
 */
const CACHE_RTL = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin] });
const CACHE_LTR = createCache({ key: "mui" });

type Params = Record<string, string | number>;
export type TFn = (key: StringKey, params?: Params) => string;

interface I18nValue {
  locale: Locale;
  dir: "rtl" | "ltr";
  setLocale: (l: Locale) => void;
  t: TFn;
}

const I18nContext = createContext<I18nValue | null>(null);

function readInitialLocale(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "fa" || v === "ps" || v === "en") return v;
  } catch {
    // localStorage can throw in private mode / blocked storage — fall through.
  }
  return "fa";
}

/**
 * Replace `{name}` placeholders. An unknown placeholder is left untouched.
 * A *number* param is written in the panel's digits (۴۰ / 40); strings pass
 * through as-is, so receipt numbers, hashes and already-formatted money are
 * never re-digitised.
 */
function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    if (!(key in params)) return match;
    const value = params[key];
    return typeof value === "number" ? formatNumber(value) : String(value);
  });
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);
  const dir: "rtl" | "ltr" = RTL_LOCALES.includes(locale) ? "rtl" : "ltr";

  // Keep the formatting layer (money, Jalali dates) in sync before children
  // render, so the first paint already uses the right digits. Idempotent.
  setFormatLocale(locale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // Best effort; the choice still applies for this session.
    }
  }, []);

  // Keep the document's lang/dir in sync so the browser, fonts and any
  // non-MUI text pick up the right direction.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  const t = useCallback<TFn>(
    (key, params) => {
      const value = DICTS[locale][key] ?? fa[key] ?? key;
      return interpolate(value, params);
    },
    [locale],
  );

  const theme = useMemo(() => createAppTheme(dir), [dir]);

  const value = useMemo<I18nValue>(
    () => ({ locale, dir, setLocale, t }),
    [locale, dir, setLocale, t],
  );

  return (
    <I18nContext.Provider value={value}>
      <CacheProvider value={dir === "rtl" ? CACHE_RTL : CACHE_LTR}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}

/** The common case: just the translator function. */
export function useT(): TFn {
  return useI18n().t;
}
